import { LineOfBusiness } from './domain.models';

export type RiskFieldType = 'text' | 'number' | 'select' | 'toggle' | 'date';

export interface RiskField {
  key: string;
  label: string;
  type: RiskFieldType;
  options?: string[];
  required?: boolean;
  hint?: string;
}

const SECURITY = ['Ninguna', 'Rejas', 'Alarma', 'Alarma monitoreada'];
const ACTIVITIES = ['Comercio minorista', 'Gastronomía', 'Oficina y servicios', 'Taller o industria liviana', 'Depósito'];

/** One risk form per line of business, valid for every company (spec §5.5, illustrative fields). */
export const RISK_FIELDS: Record<LineOfBusiness, RiskField[]> = {
  auto: [
    { key: 'zeroKm', label: '0 km', type: 'toggle' },
    { key: 'use', label: 'Uso', type: 'select', options: ['Particular', 'Comercial', 'Aplicación'], required: true },
    { key: 'gnc', label: 'Tiene GNC', type: 'toggle' },
    { key: 'postalCode', label: 'Código postal donde se guarda', type: 'text', required: true },
    { key: 'annualKm', label: 'Kilómetros anuales', type: 'number', required: true },
    { key: 'alarm', label: 'Alarma o rastreo', type: 'toggle' },
    { key: 'plate', label: 'Patente', type: 'text', hint: 'Opcional al cotizar' },
    { key: 'driverBirthDate', label: 'Nacimiento del conductor principal', type: 'date', required: true },
    { key: 'driverSex', label: 'Sexo del conductor principal', type: 'select', options: ['Femenino', 'Masculino', 'Otro'], required: true },
  ],
  home: [
    { key: 'address', label: 'Domicilio del riesgo', type: 'text', required: true },
    { key: 'dwellingType', label: 'Tipo de vivienda', type: 'select', options: ['Casa', 'Departamento', 'PH'], required: true },
    { key: 'area', label: 'Metros cuadrados', type: 'number', required: true },
    { key: 'construction', label: 'Tipo de construcción', type: 'select', options: ['Mampostería', 'Madera', 'Mixta'], required: true },
    { key: 'permanent', label: 'Vivienda permanente', type: 'toggle' },
    { key: 'security', label: 'Medidas de seguridad', type: 'select', options: SECURITY },
    { key: 'fireBuilding', label: 'Suma de incendio de edificio', type: 'number', required: true },
    { key: 'contents', label: 'Suma de contenido', type: 'number', required: true },
    { key: 'theft', label: 'Suma de robo', type: 'number', required: true },
  ],
  commerce: [
    { key: 'address', label: 'Domicilio del comercio', type: 'text', required: true },
    { key: 'activity', label: 'Actividad', type: 'select', options: ACTIVITIES, required: true },
    { key: 'area', label: 'Metros cuadrados', type: 'number', required: true },
    { key: 'security', label: 'Medidas de seguridad', type: 'select', options: SECURITY },
    { key: 'buildingSum', label: 'Suma de edificio', type: 'number', required: true },
    { key: 'contents', label: 'Suma de contenido', type: 'number', required: true },
    { key: 'merchandise', label: 'Suma de mercadería', type: 'number', required: true },
  ],
  liability: [
    { key: 'activity', label: 'Actividad', type: 'select', options: ACTIVITIES, required: true },
    { key: 'sumInsured', label: 'Suma asegurada', type: 'number', required: true },
    { key: 'employees', label: 'Cantidad de empleados', type: 'number', required: true },
    { key: 'annualRevenue', label: 'Facturación anual', type: 'number', required: true },
  ],
  'personal-accident': [
    { key: 'occupation', label: 'Actividad o profesión', type: 'text', required: true },
    { key: 'deathCapital', label: 'Capital por muerte e invalidez', type: 'number', required: true },
    { key: 'medicalExpenses', label: 'Gastos médicos', type: 'number', required: true },
    { key: 'insuredCount', label: 'Cantidad de asegurados', type: 'number', required: true },
  ],
  life: [
    { key: 'capital', label: 'Capital (producto enlatado)', type: 'select', options: ['5000000', '10000000', '20000000', '50000000'], required: true },
    { key: 'beneficiaries', label: 'Beneficiarios', type: 'text', hint: 'Opcional al cotizar' },
  ],
};

const EXTRA_LABELS: Record<string, string> = {
  vehicle: 'Vehículo',
  vehicleYear: 'Año',
  vehicleValuation: 'Valuación de mercado',
  description: 'Resumen',
};

/** Risk key whose number feeds the "suma asegurada" of an offer. */
export const SUM_INSURED_KEY: Record<LineOfBusiness, string> = {
  auto: 'vehicleValuation',
  home: 'fireBuilding',
  commerce: 'buildingSum',
  liability: 'sumInsured',
  'personal-accident': 'deathCapital',
  life: 'capital',
};

export function riskLabel(line: LineOfBusiness, key: string): string {
  return RISK_FIELDS[line].find(f => f.key === key)?.label ?? EXTRA_LABELS[key] ?? key;
}

export function riskSummaryOf(line: LineOfBusiness, risk: Record<string, string | number>): string {
  const text = (key: string) => String(risk[key] ?? '');
  switch (line) {
    case 'auto': return text('vehicle');
    case 'home': return `${text('dwellingType')} en ${text('address')}`;
    case 'commerce': return `${text('activity')} en ${text('address')}`;
    case 'liability': return `Responsabilidad civil: ${text('activity')}`;
    case 'personal-accident': return `Accidentes personales: ${text('occupation')}`;
    case 'life': return `Vida enlatado, capital ${text('capital')}`;
  }
}
